import React, { useState, useEffect, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import { apiGet } from './api';
import { syncFirstTouch } from './analytics/signup';
import { trackPageview } from './analytics/plausible';
import AdPixelGate from './analytics/AdPixelGate';

// Import Pages
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import AssistantPage from './pages/AssistantPage';
import FantasyPage from './pages/FantasyPage';
import ComparePage from './pages/ComparePage';
import PricingPage from './pages/PricingPage';
import DownloadsPage from './pages/DownloadsPage';
import BooksPage from './pages/BooksPage';
import TOSPage from './pages/TOSPage';
import PrivacyPage from './pages/PrivacyPage';
import HelpCenterPage from './pages/HelpCenterPage';
import HelpTopicPage from './pages/HelpTopicPage';
import HelpTutorialPage from './pages/HelpTutorialPage';
import HelpArticlePage from './pages/HelpArticlePage';
import HelpSupportPage from './pages/HelpSupportPage';
import AccountPage from './pages/AccountPage';
import AuthPage from './pages/AuthPage';
import RecoverPage from './pages/RecoverPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UnsubscribePage from './pages/UnsubscribePage';
import { HelmetProvider } from 'react-helmet-async';
import RelationshipPage from './pages/RelationshipPage';
import BooksPreview from './pages/BooksPreview';
import NovemberPage from './pages/NovemberPage';
import WorldPreview from './pages/WorldPreview';
import HomePage from './pages/HomePage';
import StoryboardPage from './pages/StoryboardPage';
import WritingDashboardPage from './pages/WritingDashboardPage';
import ChecklistPage from './pages/ChecklistPage';

// Import Components
import ProtectedRoute from './components/ProtectedRoute';
import { embedSessionLoad, isEmbedLocation } from './desktopAuthBootstrap';
import SignOutRoute from './components/SignOutRoute';
import SkipContactVerification from './components/SkipContactVerification';
import { lazyWithReload } from './utils/lazyWithReload';
import { takeReturnTo, peekReturnTo } from './utils/returnTo';
import { peekRefreshSignOutPage, takeRefreshSignOutPage } from './auth/rememberOnRefreshFailure';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import Footer from './components/Footer';
import TrialBanner from './components/TrialBanner';

// The admin is its own bundle (#848): the public site never downloads it.
const AdminApp = lazyWithReload(() => import('./pages/admin/AdminApp'), 'admin');
const AdminHelpRoute = lazyWithReload(() => import('./pages/admin/AdminHelpRoute'), 'admin-help');
const adminLoading = <p role="status" style={{ padding: 32 }}>Loading admin…</p>;

// Fires a Plausible pageview on every route change (and the initial load). Uses
// the full location so UTM query params on QR/campaign links are captured. No-op
// unless REACT_APP_PLAUSIBLE_DOMAIN is set.
function RouteAnalytics() {
  const { pathname, search } = useLocation();
  useEffect(() => { trackPageview(); }, [pathname, search]);
  return null;
}

// A tab whose page load started on the desktop app's embed session (#1034) that has left
// the read-only embeds: an account-state redirect, or a link. It shows this, never the site,
// so the embed's session cannot drive the writable site. Wording approved by the owner.
const EmbedExitNotice: React.FC = () => (
  <div className="embed-exit-notice">
    <p>This page opens in your browser.</p>
    <p>Go to <a href={`${window.location.origin}/`} target="_blank" rel="noopener noreferrer">trychaptery.com</a> to continue.</p>
  </div>
);

// Separated from App so it can use useAuthenticator (must be inside Authenticator.Provider)
function AppContent() {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [trial, setTrial] = useState<{ isTrial: boolean; daysRemaining: number | null } | null>(null);
  // Defaults to true so the prompt never flashes before the session has been read.
  const [emailVerified, setEmailVerified] = useState(true);
  // The ID token's sub, for the pages that need the account (the home page).
  const [accountId, setAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // True once this sign-in's access check has answered (#948).
  const [accessChecked, setAccessChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isReadonly = new URLSearchParams(location.search).get('readonly') === 'true';
  const isChromeless = isReadonly;

  useEffect(() => {
    if (authStatus === 'authenticated') {
      // The sign-in page left `loading` false. Until the checks answer, protected pages
      // must wait as on a reload, or /books shows its subscribe preview first (#1014).
      setLoading(true);
      setAccessChecked(false);
      // A sign-out before these checks answer hands the flag to the next sign-in's
      // checks; a late answer from this one must not end that wait early (#1014).
      let current = true;
      const access = checkAccess();
      access.finally(() => {
        if (!current) return;
        setLoading(false);
        setAccessChecked(true);
      });
      // Attach the first touch and report a new sign-up (#937), only AFTER the access
      // check. /profile?acq sits behind the subscription gate, and a brand-new account's
      // trial row is granted lazily by /subscriptions/status, so a /profile sent
      // alongside it can 403 and nothing would retry until the next full page load.
      // Fire and forget: the page never waits on it. See signup.ts.
      access.then((id) => syncFirstTouch(apiGet, id)).catch(() => { /* measurement never breaks the app */ });
      return () => { current = false; };
    } else if (authStatus === 'unauthenticated') {
      setIsAdmin(false);
      setIsSubscriber(false);
      setTrial(null);
      setAccessChecked(false);
      // Reset to the default (verified), so the next sign-in does not start from the
      // previous user's state and flash the Account page's verify section before
      // checkAccess resolves.
      setEmailVerified(true);
      setAccountId(null);
      setLoading(false);
    }
  }, [authStatus]);

  // Back to the page a signed-out visitor was going to (#948), such as the download
  // email's /downloads. Google and Apple sign-in return to the site's root, so this
  // finishes their trip from there, once the checks have answered. An email sign-in goes
  // there from AuthPage. Anywhere else, the person chose the page they are on, so it is
  // only forgotten.
  useEffect(() => {
    if (authStatus !== 'authenticated' || !accessChecked) return;
    const to = takeReturnTo();
    if (to && location.pathname === '/') navigate(to, { replace: true });
  }, [authStatus, accessChecked, location.pathname, navigate]);

  // Back to the page a lapsed session was on (#1039). A Google or Apple account whose
  // session could not be refreshed was signed out through Cognito's /logout, which lands
  // on the root. Signed out, that page shows as it would to anyone; a protected one sends
  // them to sign in and back. See rememberOnRefreshFailure.
  //
  // Read as the load starts, not when the effect runs: until the move, the root must not
  // count as a signed-out visitor on a marketing page, or AdPixelGate (a child, whose
  // effects run first) would load the ad pixel for a returning customer (#812). The note
  // is used up once auth is known; a signed-in return (Google's sign-in comes back here)
  // just drops it.
  const [lapsedPage, setLapsedPage] = useState(() => (window.location.pathname === '/' ? peekRefreshSignOutPage() : null));
  const returningToLapsedPage = lapsedPage !== null && location.pathname === '/' && authStatus !== 'authenticated';
  useEffect(() => {
    if (location.pathname !== '/' || (authStatus !== 'unauthenticated' && authStatus !== 'authenticated')) return;
    const page = takeRefreshSignOutPage();
    setLapsedPage(null);
    if (page && authStatus === 'unauthenticated') navigate(page, { replace: true });
  }, [authStatus, location.pathname, navigate]);

  // In the desktop read-only embed, suppress the browser's native right-click
  // menu (Back/Reload/Save image/Inspect…) across the whole window — it has no
  // place in an embedded read-only control.
  useEffect(() => {
    if (!isChromeless) return;
    const blockContextMenu = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('contextmenu', blockContextMenu);
    return () => document.removeEventListener('contextmenu', blockContextMenu);
  }, [isChromeless]);

  // Subscription state comes from GET /subscriptions/status, NOT the cognito:groups
  // claim. The server deliberately distrusts that claim: it goes stale after a trial
  // expires (the token keeps `Subscribers` until it refreshes and re-evaluates groups),
  // which let expired accounts past this gate and straight into 403s on every call, and
  // kept brand-new subscribers locked out until their token refreshed. Admin genuinely
  // IS a group concept, so it still reads the claim.
  // Resolves to the account id (the ID token's sub), or null when the session could not be read.
  const checkAccess = async (): Promise<string | null> => {
    let groups: string[] = [];
    let adminStatus = false;
    let sub: string | null = null;

    try {
      const session = await fetchAuthSession();
      groups = (session.tokens?.accessToken.payload['cognito:groups'] as string[]) || [];
      adminStatus = groups.includes('Admins') || groups.includes('SuperAdmins');
      setIsAdmin(adminStatus);
      // Signup no longer proves the address (#697), so this is genuinely false for
      // many accounts rather than a formality. Federated users arrive verified because the Google and Apple providers map email_verified (#893).
      const claims = session.tokens?.idToken?.payload ?? {};
      setEmailVerified(claims['email_verified'] === true || claims['email_verified'] === 'true');
      sub = typeof claims['sub'] === 'string' ? claims['sub'] : null;
      setAccountId(sub);
    } catch (err) {
      console.error('Error reading auth session:', err);
      setIsAdmin(false);
      setIsSubscriber(false);
      return null;
    }

    try {
      const res = await apiGet('/subscriptions/status');
      const data = await res.body.json() as {
        active?: boolean; isTrial?: boolean; trialDaysRemaining?: number | null;
      };
      setIsSubscriber(adminStatus || data.active === true);
      setTrial({ isTrial: data.isTrial === true, daysRemaining: data.trialDaysRemaining ?? null });
    } catch (err) {
      // This value used to be read straight off the token, so it could not fail on a
      // network blip. Fall back to the (possibly stale) claim rather than locking a
      // paying user out of their own books — the server is the real gate and still
      // returns 403 if the claim turns out to be wrong.
      console.error('Subscription status unavailable; falling back to token claim:', err);
      setIsSubscriber(adminStatus || groups.includes('Subscribers'));
    }
    return sub;
  };

  // An embed-session tab off the read-only embeds shows the notice, not the site (#1034).
  if (embedSessionLoad && !isEmbedLocation(location.pathname, location.search)) {
    return <div className="App"><EmbedExitNotice /></div>;
  }

  return (
    <div className="App">
      <ScrollToTop />
      {/* Sign Out works after an unverified email sign-in (#1044). */}
      <SkipContactVerification />
      <RouteAnalytics />
      <AdPixelGate signedOut={authStatus === 'unauthenticated' && !returningToLapsedPage} />
      {!isChromeless && <Header isAdmin={isAdmin} />}
      {!isChromeless && authStatus === 'authenticated' && (
        <TrialBanner isTrial={trial?.isTrial ?? false} daysRemaining={trial?.daysRemaining ?? null} />
      )}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          // Blank while a sign-in that came back here is about to move on (#948), or a
          // lapsed session is being taken back to its page (#1039), so the home page
          // doesn't flash and fetch on the way.
          loading || returningToLapsedPage || (authStatus === 'authenticated' && peekReturnTo())
            ? <div className="auth-page-container container" />
            : authStatus === 'authenticated'
              ? <HomePage accountId={accountId} isSubscriber={isSubscriber} />
              : <LandingPage />
        } />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/features/assistant" element={<AssistantPage />} />
        <Route path="/fantasy-writing-software" element={<FantasyPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/november" element={<NovemberPage />} />
        <Route path="/pricing" element={<PricingPage onEmailVerified={() => setEmailVerified(true)} />} />

        {/* Auth Routes */}
        <Route path="/login" element={<AuthPage initialState="signIn" />} />
        <Route path="/register" element={<AuthPage initialState="signUp" />} />
        <Route path="/recover" element={<RecoverPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* The recovery key left sign-up (#1105); it is made on the Account page now. */}
        <Route path="/setup-recovery-key" element={<Navigate to="/account" replace />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<Navigate to="/books" replace />} />

        <Route path="/account" element={
          <ProtectedRoute isAdmin={isAdmin} isSubscriber={isSubscriber} loading={loading}>
            <AccountPage emailVerified={emailVerified} onEmailVerified={() => setEmailVerified(true)} />
          </ProtectedRoute>
        } />

        <Route path="/books" element={
          <ProtectedRoute
            requireSubscription={true}
            isAdmin={isAdmin}
            isSubscriber={isSubscriber}
            loading={loading}
            fallback={<BooksPreview />}
          >
            <BooksPage accountId={accountId} />
          </ProtectedRoute>
        } />

        <Route path="/world" element={
          <ProtectedRoute
            requireSubscription={true}
            isAdmin={isAdmin}
            isSubscriber={isSubscriber}
            loading={loading}
            fallback={<WorldPreview />}
          >
            <RelationshipPage />
          </ProtectedRoute>
        } />

        <Route path="/downloads" element={
          <ProtectedRoute
            isAdmin={isAdmin}
            isSubscriber={isSubscriber}
            loading={loading}
          >
            <DownloadsPage isSubscriber={isSubscriber} />
          </ProtectedRoute>
        } />

        <Route path="/checklist" element={
          <ProtectedRoute isAdmin={isAdmin} isSubscriber={isSubscriber} loading={loading}>
            <ChecklistPage />
          </ProtectedRoute>
        } />

        <Route path="/writing-dashboard" element={
          <ProtectedRoute
            requireSubscription={true}
            isAdmin={isAdmin}
            isSubscriber={isSubscriber}
            loading={loading}
          >
            <WritingDashboardPage />
          </ProtectedRoute>
        } />

        <Route path="/relationships" element={<Navigate to="/world" replace />} />

        <Route path="/admin/help" element={
          <ProtectedRoute
            requireAdmin={true}
            isAdmin={isAdmin}
            isSubscriber={isSubscriber}
            loading={loading}
          >
            <Suspense fallback={adminLoading}><AdminHelpRoute /></Suspense>
          </ProtectedRoute>
        } />

        <Route path="/admin/*" element={
          <ProtectedRoute
            requireAdmin={true}
            isAdmin={isAdmin}
            isSubscriber={isSubscriber}
            loading={loading}
          >
            <Suspense fallback={adminLoading}><AdminApp /></Suspense>
          </ProtectedRoute>
        } />

        {/* Legal / Help Routes */}
        <Route path="/tos" element={<TOSPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/help/topic/:slug" element={<HelpTopicPage />} />
        <Route path="/help/tutorial" element={<HelpTutorialPage />} />
        <Route path="/help/support" element={<HelpSupportPage />} />
        <Route path="/help/article/:slug" element={<HelpArticlePage />} />

        {/* Desktop client webview routes (no site chrome) */}

        <Route path="/storyboard" element={
          <ProtectedRoute
            requireSubscription={true}
            isAdmin={isAdmin}
            isSubscriber={isSubscriber}
            loading={loading}
          >
            <StoryboardPage />
          </ProtectedRoute>
        } />

        {/* Sign out is a route so unsaved work can stop it (#848) */}
        <Route path="/signout" element={<SignOutRoute />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isChromeless && <Footer />}
    </div>
  );
}

// A render error anywhere in the app lands here rather than on a blank page. React
// Router already logs it.
function RouteError() {
  return (
    <div style={{ padding: '15vh 24px', textAlign: 'center' }}>
      <h1>Something went wrong</h1>
      <p>Reload the page to try again.</p>
    </div>
  );
}

function App() {
  // A data router, because only a data router can block navigation, and the admin asks
  // before a page with unsaved work is left (#848). The whole existing app sits in one
  // catch-all route, so every <Routes> below works as it did under <BrowserRouter>.
  // Made on mount rather than at import: the app tests render App at a different
  // address in each test, and the router reads the address when it is made.
  // StrictMode makes a spare router in dev; it is harmless, so it is not disposed.
  const [router] = useState(() => createBrowserRouter([
    {
      path: '*',
      element: <AppContent />,
      errorElement: <RouteError />,
    },
  ]));
  return (
    <HelmetProvider>
      {/* No app-level default <meta name="description"> on purpose: an outer Helmet
          is emitted BEFORE the page's own tag, and search engines read the first
          one — which would shadow every page-specific description. Each public page
          sets its own title + description instead. */}
      <Authenticator.Provider>
        <RouterProvider router={router} />
      </Authenticator.Provider>
    </HelmetProvider>
  );
}

export default App;
